import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Link2, 
  MousePointerClick, 
  Users, 
  QrCode,
  Calendar as CalendarIcon,
  Download,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { id } from "date-fns/locale";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

const COLORS = ["#7c3aed", "#a855f7", "#c084fc", "#d8b4fe", "#e9d5ff"];

const GlobalAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 7),
    to: new Date()
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/global`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Gagal memuat analytics");
    } finally {
      setIsLoading(false);
    }
  };

  const exportData = () => {
    if (!analytics) return;
    
    // Create CSV content
    let csv = "Metric,Value\n";
    csv += `Total Links,${analytics.total_links}\n`;
    csv += `Total Clicks,${analytics.total_clicks}\n`;
    csv += `Unique Visitors,${analytics.unique_visitors}\n`;
    csv += `QR Code Visitors,${analytics.qr_visitors}\n`;
    csv += "\n\nClicks by Date\nDate,Clicks\n";
    analytics.clicks_by_date.forEach(d => {
      csv += `${d.date},${d.clicks}\n`;
    });
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `analytics-${format(new Date(), "yyyy-MM-dd")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Data berhasil diexport!");
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    { icon: Link2, label: "Total Links", value: analytics?.total_links || 0, color: "text-primary" },
    { icon: MousePointerClick, label: "Visitors", value: analytics?.total_clicks || 0, color: "text-red-500" },
    { icon: Users, label: "Unique Visitor", value: analytics?.unique_visitors || 0, color: "text-green-500" },
    { icon: QrCode, label: "QR Code Visitor", value: analytics?.qr_visitors || 0, color: "text-blue-500" }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="global-analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Analytics
            </h1>
            <p className="text-muted-foreground mt-1">
              Overview performa semua link Anda
            </p>
          </div>
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="date-range-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(dateRange.from, "dd MMM", { locale: id })} - {format(dateRange.to, "dd MMM yyyy", { locale: id })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" onClick={exportData} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl bg-muted ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                      {stat.value.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Timeline Chart */}
        <Card className="dashboard-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Timeline</CardTitle>
              <CardDescription>Performa klik dari waktu ke waktu</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {analytics?.clicks_by_date?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.clicks_by_date}>
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Empty Data</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Devices */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Devices</CardTitle>
              <CardDescription>Distribusi perangkat pengunjung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {analytics?.devices?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.devices}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {analytics.devices.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Belum ada data
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Browsers */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Browsers</CardTitle>
              <CardDescription>Browser yang digunakan pengunjung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {analytics?.browsers?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.browsers} layout="vertical">
                      <XAxis type="number" hide />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={80}
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Belum ada data
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Links */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg">Top Performing Links</CardTitle>
            <CardDescription>Link dengan performa terbaik</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.top_links?.length > 0 ? (
              <div className="space-y-4">
                {analytics.top_links.map((link, index) => (
                  <div key={link.link_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{link.title}</p>
                        <p className="text-xs text-muted-foreground">/{link.short_code}</p>
                      </div>
                    </div>
                    <span className="font-bold text-primary">{link.clicks.toLocaleString()} clicks</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Link2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Belum ada data link</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default GlobalAnalyticsPage;
